import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getProfile } from "../services/users.ts";
import { confirmPayment } from "../services/payments.ts";
import StripeCheckout from "../components/StripeCheckout.tsx";
import TopNav, { AppNavMode } from "../components/TopNav.tsx";

interface ProfileData {
  email: string;
  country: string | null;
  subscription_active: boolean;
  subscription_ends_at: string | null;
  payment_status: string | null;
  can_renew: boolean;
  role: string;
}

// Mirrors app/core/pricing.py::get_price_for_country - only used here to label the
// Stripe button; the amount actually charged is decided server-side.
const getSubscriptionFee = (country: string | null) => {
  switch (country) {
    case "UK":
      return { amount: 700, currency: "gbp", label: "£7" };
    case "India":
      return { amount: 1000 * 100, currency: "inr", label: "Rs. 1000" };
    case "US":
      return { amount: 1000, currency: "usd", label: "$10" };
    default:
      return { amount: 700, currency: "gbp", label: "£7" };
  }
};

const Profile: React.FC = () => {
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showStripe, setShowStripe] = useState(false);
  const [renewMessage, setRenewMessage] = useState("");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectedForRenewal = searchParams.get("reason") === "subscription_inactive";

  const userId = localStorage.getItem("user_id");

  const fetchProfile = async () => {
    if (!userId) {
      setError("Not logged in");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const { data } = await getProfile(userId);
      setProfile(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const stopPolling = () => {
    if (pollTimer.current) {
      clearInterval(pollTimer.current);
      pollTimer.current = null;
    }
  };

  useEffect(() => {
    fetchProfile();
    return stopPolling;
  }, []);

  const handleModeNav = (mode: AppNavMode) => {
    if (mode === "practice-exam") {
      navigate("/practice-exam");
      return;
    }
    navigate(`/questions?mode=${mode}`);
  };

  // Renewal only counts once the backend has actually recorded it, not just once Stripe
  // reports success - subscription_active alone can't tell us that (an early renewal starts
  // from an already-active subscription), so wait for the expiry date to move forward.
  const pollForRenewal = (wasActive: boolean, previousEndsAt: string | null) => {
    if (!userId) return;
    setRenewMessage("Payment received. Confirming renewal...");
    let attempts = 0;
    stopPolling();
    pollTimer.current = setInterval(async () => {
      attempts += 1;
      try {
        const { data } = await getProfile(userId);
        const renewed = data.subscription_active && (!wasActive || data.subscription_ends_at !== previousEndsAt);
        if (renewed) {
          stopPolling();
          setProfile(data);
          setRenewMessage("Membership renewed!");
          navigate("/profile", { replace: true });
        } else if (attempts >= 10) {
          stopPolling();
          setProfile(data);
          setRenewMessage(
            "Payment received. Renewal is taking longer than usual - refresh this page in a minute, or contact support if it doesn't complete."
          );
        }
      } catch {
        // Transient network error while polling; keep trying until attempts run out.
      }
    }, 2000);
  };

  const handleRenewSuccess = async (paymentIntentId: string) => {
    setShowStripe(false);
    if (!profile) return;
    const wasActive = profile.subscription_active;
    const previousEndsAt = profile.subscription_ends_at;
    try {
      const { data } = await confirmPayment(profile.email, paymentIntentId);
      if (data.status === "active") {
        setRenewMessage("Membership renewed!");
        navigate("/profile", { replace: true });
        fetchProfile();
        return;
      }
    } catch {
      // /confirm failing here doesn't mean the payment failed - the webhook is the
      // authoritative fallback. Poll below instead of declaring success or failure.
    }
    pollForRenewal(wasActive, previousEndsAt);
  };

  const handleRenewFailure = (msg: string) => {
    setShowStripe(false);
    setRenewMessage(msg || "Payment failed. Membership not renewed.");
  };

  const handleRenewUncertain = () => {
    setShowStripe(false);
    setRenewMessage("We couldn't confirm your payment status. Refresh in a minute to check.");
  };

  if (loading) {
    return <div className="text-xl font-body text-gray-500 text-center py-16">Loading...</div>;
  }

  if (error || !profile) {
    return <div className="text-xl font-body text-incorrect text-center py-16">{error || "Profile unavailable"}</div>;
  }

  const fee = getSubscriptionFee(profile.country);
  const expiryLabel = profile.subscription_ends_at
    ? new Date(profile.subscription_ends_at).toLocaleDateString()
    : "N/A";

  return (
    <div className="min-h-screen">
      <TopNav activeMode="profile" onSelect={handleModeNav} />
      <div className="flex flex-col items-center px-6 py-16">
        <div className="bg-white rounded-lg border border-border p-8 w-full max-w-lg">
          <h2 className="text-4xl font-bold mb-6 text-ink font-display text-center">Profile</h2>
          {redirectedForRenewal && !profile.subscription_active && (
            <div className="mb-6 text-lg font-body text-incorrect text-center bg-lightbg border border-border rounded-lg p-3">
              Your membership needs to be renewed before you can continue.
            </div>
          )}
          <table className="text-xl font-body text-ink">
            <tbody>
              <tr>
                <td className="text-gray-500 pr-10 py-1 align-top whitespace-nowrap">Email</td>
                <td className="py-1">{profile.email}</td>
              </tr>
              <tr>
                <td className="text-gray-500 pr-10 py-1 align-top whitespace-nowrap">Country</td>
                <td className="py-1">{profile.country || "N/A"}</td>
              </tr>
              <tr>
                <td className="text-gray-500 pr-10 py-1 align-top whitespace-nowrap">Membership status</td>
                <td className="py-1">{profile.subscription_active ? "Active" : "Inactive"}</td>
              </tr>
              <tr>
                <td className="text-gray-500 pr-10 py-1 align-top whitespace-nowrap">Valid Until</td>
                <td className="py-1">{expiryLabel}</td>
              </tr>
            </tbody>
          </table>

          {renewMessage && (
            <div className="mt-4 text-lg font-body text-primary text-center">{renewMessage}</div>
          )}

          {profile.can_renew && (
            <div className="mt-6 flex justify-center">
              <button
                type="button"
                onClick={() => setShowStripe(true)}
                className="bg-primary text-white text-xl font-semibold font-body py-2 px-8 rounded-lg hover:bg-ink transition-colors duration-200"
              >
                Renew membership
              </button>
            </div>
          )}

          <div className="mt-6 flex justify-center">
            <button
              type="button"
              onClick={() => navigate("/change-password")}
              className="text-lg font-semibold font-body text-gray-500 hover:text-ink transition-colors duration-200"
            >
              Change Password
            </button>
          </div>
        </div>
      </div>

      {showStripe && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-lg w-full relative border border-border">
            <button
              className="absolute top-2 right-2 text-gray-400 hover:text-ink text-3xl font-bold"
              onClick={() => setShowStripe(false)}
            >
              ×
            </button>
            <StripeCheckout
              amount={fee.amount}
              currency={fee.currency}
              country={profile.country || ""}
              email={profile.email}
              onSuccess={handleRenewSuccess}
              onFailure={handleRenewFailure}
              onUncertain={handleRenewUncertain}
              description={`Renew Membership (${fee.label}/month)`}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
